
import { AlertCircle, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, SkipForward, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useCallback, useState, useRef, useMemo } from 'react';
import {
  extractDisplayRow,
  getDisplayColumns,
  getDisplayFields,
  getIdempotencySummary,
  getImportPreviewColumns,
  getImportRowLineNumber,
  getImportRowStatus,
} from '@/lib/importFieldLabels';
import { normalizeIdempotency } from '@/lib/idempotencyNormalizer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ImportErrorDetails from './ImportErrorDetails';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export interface ImportError {
  rowNumber: number;
  columnName: string;
  receivedValue: string;
  rule: string;
  message: string;
}

export interface IdempotencyItem {
  rowNumber: number;
  status: 'NEW' | 'SKIPPED' | 'CONFLICTED';
  data: Record<string, any>;
  divergentFields?: string[];
  message?: string;
}

export interface ImportResultPanelProps {
  totalRows: number;
  validRowsCount: number;
  invalidRowsCount: number;
  errors: ImportError[];
  importedPreview: Record<string, any>[];
  importId: string;
  importHistoryId?: number;
  idempotencyReport?: {
    new: IdempotencyItem[];
    skipped: IdempotencyItem[];
    conflicted: IdempotencyItem[];
  };
  onConfirm?: () => void;
  onDiscard?: () => void;
  isConfirming?: boolean;
  onConfirmationSuccess?: (result: any) => void;
}

export default function ImportResultPanel({
  totalRows,
  validRowsCount,
  invalidRowsCount,
  onDiscard,
  errors = [],
  importedPreview = [],
  importId,
  importHistoryId,
  idempotencyReport,
  onConfirm,
  isConfirming = false,
  onConfirmationSuccess,
}: ImportResultPanelProps) {
  const [expandedIdempotency, setExpandedIdempotency] = useState<Set<string>>(new Set());
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const refetchScheduledRef = useRef(false);
  
  const confirmMutation = trpc.imports.confirmImport.useMutation();
  const utils = trpc.useUtils();
  
  const handleConfirmImport = useCallback(async () => {
    if (!importHistoryId) {
      setConfirmationError('ID de importacao nao encontrado');
      return;
    }
    
    try {
      setConfirmationError(null);
      refetchScheduledRef.current = false;
      const result = await confirmMutation.mutateAsync({ importHistoryId });
      setConfirmationResult(result);
      
      if (!refetchScheduledRef.current) {
        refetchScheduledRef.current = true;
        setTimeout(() => {
          utils.imports.getHistory.invalidate();
          refetchScheduledRef.current = false;
        }, 500);
      }
      
      onConfirmationSuccess?.(result);
    } catch (error: any) {
      const errorMsg = error?.message || 'Erro ao confirmar importacao';
      setConfirmationError(errorMsg);
      console.error('Erro ao confirmar:', error);
    }
  }, [importHistoryId, confirmMutation, onConfirmationSuccess, utils]);
  
  const successRate = totalRows > 0 ? Math.round((validRowsCount / totalRows) * 100) : 0;
  
  // Garantir que arrays não são undefined
  const safeErrors = Array.isArray(errors) ? errors : [];
  const safeImportedPreview = Array.isArray(importedPreview) ? importedPreview : [];
  
  // Garantir que o primeiro item de importedPreview é um objeto válido
  const firstImportedRow = safeImportedPreview.length > 0 && safeImportedPreview[0] && typeof safeImportedPreview[0] === 'object' ? safeImportedPreview[0] : null;
  
  // Usar mapeamento de labels humanas em vez de chaves técnicas
  const displayColumns = getDisplayColumns(safeImportedPreview);
  const previewColumns = getImportPreviewColumns(safeImportedPreview, 6);
  const importedColumns = previewColumns.length > 0 ? previewColumns : displayColumns;

  const importedRowsForDisplay = safeImportedPreview.map((row, idx) => {
    const displayRow = extractDisplayRow(row, importedColumns);
    return {
      key: String((row as any).rowHash || (row as any).id || (row as any)._rowNumber || (row as any).rowNumber || `${importId}-${idx}`),
      lineNumber: getImportRowLineNumber(row, idx + 2),
      status: getImportRowStatus(row) || 'Pronta para processamento',
      displayRow,
    };
  });
  
  // Dados de idempotência - usar normalizeIdempotency para garantir estrutura consistente
  const normalizedIdempotency = useMemo(() => {
    return normalizeIdempotency(idempotencyReport);
  }, [idempotencyReport]);
  const newCount = normalizedIdempotency.new.length;
  const skippedCount = normalizedIdempotency.skipped.length;
  const conflictedCount = normalizedIdempotency.conflicted.length;



  const toggleIdempotencyExpanded = (key: string) => {
    const newExpanded = new Set(expandedIdempotency);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedIdempotency(newExpanded);
  };

  return (
    <div className="space-y-4">
      {/* Card de Resumo */}
      <Card>
        <CardHeader>
          <CardTitle>Revisão do lote validado</CardTitle>
          <CardDescription>
            {validRowsCount} de {totalRows} linhas estão prontas para seguir após a validação inicial
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Total de Linhas */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Linhas no lote</div>
              <div className="text-2xl font-bold text-slate-900">{totalRows}</div>
            </div>

            {/* Linhas Válidas */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">Prontas para seguir</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{validRowsCount}</div>
            </div>

            {/* Linhas Inválidas */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-700">Com pendências</span>
              </div>
              <div className="text-2xl font-bold text-red-900">{invalidRowsCount}</div>
            </div>

            {/* Taxa de Sucesso */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-700 mb-1">Aproveitamento do lote</div>
              <div className="text-2xl font-bold text-blue-900">{successRate}%</div>
            </div>
          </div>

          {/* Alert de Status */}
          {invalidRowsCount > 0 && (
            <Alert className="mt-4 border-yellow-200 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                {invalidRowsCount} linha(s) precisam de ajuste. Revise as pendências abaixo, corrija a planilha e reenvie o lote.
              </AlertDescription>
            </Alert>
          )}

          {invalidRowsCount === 0 && validRowsCount > 0 && (
            <Alert className="mt-4 border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                O lote foi validado com sucesso. Revise a prévia abaixo e clique em "Concluir importação" para processar os dados.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Botões de Ação */}
          {invalidRowsCount === 0 && validRowsCount > 0 && !confirmationResult && (
            <div className="mt-6 flex gap-3">
              <Button
                onClick={handleConfirmImport}
                disabled={confirmMutation.isPending || isConfirming}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {confirmMutation.isPending || isConfirming ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  'Concluir importação'
                )}
              </Button>
              <Button
                onClick={onDiscard}
                variant="outline"
                disabled={confirmMutation.isPending || isConfirming}
              >
                Descartar lote
              </Button>
            </div>
          )}
          
          {/* Erro de Confirmação */}
          {confirmationError && (
            <Alert className="mt-4 border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {confirmationError}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Resumo de Confirmação */}
          {confirmationResult && (
            <Card className="mt-6 border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-900">✓ Importação concluída</CardTitle>
                <CardDescription className="text-green-800">{confirmationResult.message}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-white rounded border border-green-200">
                    <div className="text-xs text-slate-600 mb-1">Total processado</div>
                    <div className="text-xl font-bold text-slate-900">{confirmationResult.totalRows}</div>
                  </div>
                  <div className="p-3 bg-white rounded border border-green-200">
                    <div className="text-xs text-slate-600 mb-1">Linhas aprovadas</div>
                    <div className="text-xl font-bold text-green-900">{confirmationResult.validRows}</div>
                  </div>
                  <div className="p-3 bg-white rounded border border-green-200">
                    <div className="text-xs text-slate-600 mb-1">Novas linhas criadas</div>
                    <div className="text-xl font-bold text-blue-900">{confirmationResult.insertedRows}</div>
                  </div>
                  <div className="p-3 bg-white rounded border border-green-200">
                    <div className="text-xs text-slate-600 mb-1">Linhas já existentes</div>
                    <div className="text-xl font-bold text-yellow-900">{confirmationResult.skippedRows}</div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded border border-green-200">
                  <div className="text-sm font-semibold text-slate-900 mb-1">Resultado final</div>
                  <div className="text-lg font-bold text-green-900">{confirmationResult.statusFinal}</div>
                </div>
                {/* V87: Aviso de sync multi-empresa */}
                {confirmationResult.pendingsSyncWarning && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-300">
                    <div className="flex items-center gap-2 text-yellow-800 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{confirmationResult.pendingsSyncWarning}</span>
                    </div>
                  </div>
                )}
                {/* V87: Confirmação de pendências geradas para multi-empresa */}
                {!confirmationResult.pendingsSyncWarning && confirmationResult.insertedRows > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-800 text-sm">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>Pendências de avaliação geradas com sucesso para todas as empresas importadas.</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Resumo de Verificação de Duplicidade */}
      {idempotencyReport && (newCount > 0 || skippedCount > 0 || conflictedCount > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Leitura de registros já existentes</CardTitle>
            <CardDescription>Veja como o lote foi classificado antes do processamento final</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Novas Linhas */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700">Novas no lote</span>
                </div>
                <div className="text-2xl font-bold text-blue-900">{newCount}</div>
                <p className="text-xs text-blue-600 mt-1">Serão processadas como novos registros</p>
              </div>

              {/* Linhas Ignoradas */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-1">
                  <SkipForward className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-700">Já existentes</span>
                </div>
                <div className="text-2xl font-bold text-yellow-900">{skippedCount}</div>
                <p className="text-xs text-yellow-600 mt-1">Já existem no ciclo atual e não serão sobrescritas</p>
              </div>

              {/* Linhas em Conflito */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-700">Conflitos</span>
                </div>
                <div className="text-2xl font-bold text-orange-900">{conflictedCount}</div>
                <p className="text-xs text-orange-600 mt-1">Dados divergentes detectados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs para Detalhes */}
      <Tabs defaultValue="importados" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="importados">
            Importados ({safeImportedPreview.length})
          </TabsTrigger>
          <TabsTrigger value="erros">
            Erros ({safeErrors.length})
          </TabsTrigger>
          {idempotencyReport && (newCount > 0 || skippedCount > 0 || conflictedCount > 0) && (
            <TabsTrigger value="idempotencia">
              Duplicidade ({newCount + skippedCount + conflictedCount})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab: Importados */}
        <TabsContent value="importados">
          <Card>
            <CardHeader>
              <CardTitle>Linhas inspecionadas</CardTitle>
              <CardDescription>
                {safeImportedPreview.length} linhas foram validadas e estão prontas para importação. Esta leitura replica as informações úteis da preparação do lote para dupla checagem antes do processamento.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {importedRowsForDisplay.length > 0 ? (
                <>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    Confira a linha, a situação operacional e os principais dados da planilha exatamente na etapa final do lote, sem depender apenas da preparação inicial.
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Linha</TableHead>
                          <TableHead>Status</TableHead>
                          {importedColumns.map((column) => (
                            <TableHead key={column}>{column}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importedRowsForDisplay.slice(0, 20).map((row) => (
                          <TableRow key={row.key}>
                            <TableCell className="font-medium text-slate-900">#{row.lineNumber}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                                {row.status}
                              </Badge>
                            </TableCell>
                            {importedColumns.map((column) => (
                              <TableCell key={`${row.key}-${column}`} className="align-top text-sm text-slate-700">
                                <div className="max-w-xs whitespace-pre-wrap break-words leading-5">
                                  {row.displayRow[column] || '—'}
                                </div>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {importedRowsForDisplay.length > 20 && (
                    <p className="text-center text-xs text-slate-500">
                      Mostrando 20 de {importedRowsForDisplay.length} linhas inspecionadas desta revisão.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-slate-500">Nenhuma linha para exibir</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Tab: Erros */}
        {safeErrors.length > 0 && (
          <TabsContent value="erros">
            <ImportErrorDetails errors={safeErrors} />
          </TabsContent>
        )}

        {/* Tab: Verificação de Duplicidade */}
        {idempotencyReport && (newCount > 0 || skippedCount > 0 || conflictedCount > 0) && (
          <TabsContent value="idempotencia">
            <Card>
              <CardHeader>
                <CardTitle>Validação de Registros Existentes</CardTitle>
                <CardDescription>
                  Classificação detalhada das linhas por status de processamento
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Novas Linhas */}
                  {normalizedIdempotency.new && normalizedIdempotency.new.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">
                        Novas Linhas ({normalizedIdempotency.new.length})
                      </h4>
                      <div className="space-y-2">
                        {normalizedIdempotency.new.map((item, idx) => (
                          <Card key={`new-${idx}`} className="border-blue-200 bg-blue-50">
                            <CardContent className="pt-4">
                              <button
                                onClick={() => toggleIdempotencyExpanded(`new-${idx}`)}
                                className="w-full text-left flex items-center justify-between hover:bg-blue-100 p-2 rounded"
                              >
                                <div>
                                  <div className="font-semibold text-blue-900">Linha {item.rowNumber}</div>
                                  <div className="text-sm text-blue-700">Status: NEW - Será criada</div>
                                </div>
                                {expandedIdempotency.has(`new-${idx}`) ? (
                                  <ChevronUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-blue-600" />
                                )}
                              </button>
                              {expandedIdempotency.has(`new-${idx}`) && (
                                <div className="mt-3 p-2 bg-white rounded text-sm">
                                  {item.data && typeof item.data === 'object' ? (() => {
                                    const summary = getIdempotencySummary(item.data);
                                    return (
                                      <div className="space-y-2">
                                        <div className="font-semibold text-slate-800">Resumo da Linha</div>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="font-semibold text-slate-700">Setor:</span>
                                            <span className="text-slate-600">{summary.setor}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="font-semibold text-slate-700">Cargo:</span>
                                            <span className="text-slate-600">{summary.cargo}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="font-semibold text-slate-700">Qtd (vidas):</span>
                                            <span className="text-slate-600">{summary.quantidade}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })() : <div className="text-slate-500">Sem dados para exibir</div>}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Linhas Ignoradas */}
                  {normalizedIdempotency.skipped && normalizedIdempotency.skipped.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-2">
                        Linhas Ignoradas ({normalizedIdempotency.skipped.length})
                      </h4>
                      <div className="space-y-2">
                        {normalizedIdempotency.skipped.map((item, idx) => (
                          <Card key={`skipped-${idx}`} className="border-yellow-200 bg-yellow-50">
                            <CardContent className="pt-4">
                              <button
                                onClick={() => toggleIdempotencyExpanded(`skipped-${idx}`)}
                                className="w-full text-left flex items-center justify-between hover:bg-yellow-100 p-2 rounded"
                              >
                                <div>
                                  <div className="font-semibold text-yellow-900">Linha {item.rowNumber}</div>
                                  <div className="text-sm text-yellow-700">Status: SKIPPED - Duplicada (não sobrescreve)</div>
                                </div>
                                {expandedIdempotency.has(`skipped-${idx}`) ? (
                                  <ChevronUp className="w-4 h-4 text-yellow-600" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-yellow-600" />
                                )}
                              </button>
                              {expandedIdempotency.has(`skipped-${idx}`) && (
                                <div className="mt-3 p-2 bg-white rounded text-sm space-y-2">
                                  <div className="space-y-1">
                                    {item.data && typeof item.data === 'object' ? Object.entries(getDisplayFields(item.data)).map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="font-semibold text-slate-700">{key}:</span>
                                        <span className="text-slate-600">{String(value).substring(0, 40)}</span>
                                      </div>
                                    )) : <div className="text-slate-500">Sem dados para exibir</div>}
                                  </div>
                                  {item.message && (
                                    <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-900">
                                      {item.message}
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Linhas em Conflito */}
                  {normalizedIdempotency.conflicted && normalizedIdempotency.conflicted.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-orange-900 mb-2">
                        Linhas em Conflito ({normalizedIdempotency.conflicted.length})
                      </h4>
                      <div className="space-y-2">
                        {normalizedIdempotency.conflicted.map((item, idx) => (
                          <Card key={`conflicted-${idx}`} className="border-orange-200 bg-orange-50">
                            <CardContent className="pt-4">
                              <button
                                onClick={() => toggleIdempotencyExpanded(`conflicted-${idx}`)}
                                className="w-full text-left flex items-center justify-between hover:bg-orange-100 p-2 rounded"
                              >
                                <div>
                                  <div className="font-semibold text-orange-900">Linha {item.rowNumber}</div>
                                  <div className="text-sm text-orange-700">Status: CONFLICTED - Dados divergentes</div>
                                </div>
                                {expandedIdempotency.has(`conflicted-${idx}`) ? (
                                  <ChevronUp className="w-4 h-4 text-orange-600" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-orange-600" />
                                )}
                              </button>
                              {expandedIdempotency.has(`conflicted-${idx}`) && (
                                <div className="mt-3 p-2 bg-white rounded text-sm space-y-2">
                                  <div className="space-y-1">
                                    {item.data && typeof item.data === 'object' ? Object.entries(getDisplayFields(item.data)).map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="font-semibold text-slate-700">{key}:</span>
                                        <span className="text-slate-600">{String(value).substring(0, 40)}</span>
                                      </div>
                                    )) : <div className="text-slate-500">Sem dados para exibir</div>}
                                  </div>
                                  {item.divergentFields && item.divergentFields.length > 0 && (
                                    <div className="mt-3 p-2 bg-orange-100 rounded text-sm text-orange-900">
                                      <strong>Campos divergentes:</strong> {item.divergentFields.join(', ')}
                                    </div>
                                  )}
                                  {item.message && (
                                    <div className="mt-3 p-2 bg-orange-100 rounded text-sm text-orange-900">
                                      {item.message}
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
